import React, { useEffect, useRef, useState } from "react";
import LendyIcon from "../../components/lendy/LendyIcon";
import AssistantAvatar from "../AssistantAvatar";
import ProfileSubHeader from "./ProfileSubHeader";
import { getIntroSteps } from "./lendyIntroSteps";
import "./lendyIntro.css";

/** "Say hi" to the assistant inside the sidebar, one screen at a time, opened from the dot on its profile or the new chat's avatar. */
const LendyIntro = ({ assistantName, assistantImageUrl, backLabel, onBack }) => {
  const steps = getIntroSteps(assistantName);
  const [index, setIndex] = useState(0);
  const bodyRef = useRef(null);
  const titleRef = useRef(null);
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const { id, icon, title, lede, note, points, tries } = steps[index];

  // Each screen starts at its top, and a screen reader hears its title.
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
    titleRef.current?.focus({ preventScroll: true });
  }, [index]);

  return (
    <section className="lendy-profile lendy-intro" aria-label={`Say hi to ${assistantName}`}>
      <ProfileSubHeader title={`Say hi to ${assistantName}`} backLabel={backLabel || `Back to ${assistantName}'s profile`} onBack={onBack} />

      <div ref={bodyRef} className="lendy-intro-body">
        <div key={id} className="lendy-intro-step">
          {icon ? (
            <span className="lendy-intro-stage" aria-hidden="true">
              <LendyIcon name={icon} size={26} />
            </span>
          ) : (
            <AssistantAvatar imageUrl={assistantImageUrl} size="profile" />
          )}
          <h3 ref={titleRef} tabIndex={-1}>
            {title}
          </h3>
          <p className="lendy-intro-lede">{lede}</p>
          {note && (
            <p className="lendy-intro-note">
              <LendyIcon name="check" />
              {note}
            </p>
          )}
          {points.length > 0 && (
            <ul className="lendy-intro-points">
              {points.map(({ title: pointTitle, text }) => (
                <li key={pointTitle}>
                  <b>{pointTitle}</b>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          )}
          {tries.length > 0 && (
            <div className="lendy-intro-tries">
              <span>Try asking</span>
              {tries.map((prompt) => (
                <p key={prompt}>{prompt}</p>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="lendy-intro-foot">
        <div className="lendy-intro-dots">
          {steps.map(({ id: stepId, title: stepTitle }, stepIndex) => (
            <button
              key={stepId}
              type="button"
              aria-label={`Step ${stepIndex + 1}: ${stepTitle}`}
              aria-current={stepIndex === index ? "step" : undefined}
              onClick={() => setIndex(stepIndex)}
            />
          ))}
        </div>
        {!isFirst && (
          <button type="button" className="lendy-pill-button" onClick={() => setIndex((current) => current - 1)}>
            Back
          </button>
        )}
        <button
          type="button"
          className="lendy-pill-button lendy-pill-button--go"
          onClick={isLast ? onBack : () => setIndex((current) => current + 1)}
        >
          {isLast ? "Done" : "Next"}
        </button>
      </div>
    </section>
  );
};

export default LendyIntro;
